'use client'

import { useEffect } from 'react'

import { useAuth } from '@/contexts/AuthContext'
import { setAnalyticsUserTier } from '@/lib/analytics'
import { resolveAnalyticsUserTier } from '@/lib/analytics/user-tier'

/**
 * Publishes the ambient monetization rung every funnel envelope on this page
 * carries (see setAnalyticsUserTier in lib/analytics.ts). Every flow that emits
 * scan-funnel events must call this: the ambient value is module state, so a
 * flow that never sets it reports either null or whatever the previous flow
 * left behind.
 *
 * Readiness, not just values: AuthContext publishes `user` before the
 * subscription lookup returns and reports `isPremium: false` until it does, so
 * a scan started in that window would be labelled `registered_free` for a
 * paying visitor. The rung stays `unknown` until entitlement is actually known.
 * Signed-out visitors are unaffected — no subscription lookup runs for them, so
 * the anonymous rung lands as soon as auth initialization does.
 *
 * @param emailTierObserved the flow's own observation that a signed-out browser
 *   holds EMAIL_UNLOCK_COOKIE (the analyze route's 429 `tier`, or an
 *   email-tier ceiling on a success payload). Omitted, the persisted hint in
 *   lib/analytics/user-tier.ts still applies.
 */
export function useAnalyticsUserTier(emailTierObserved = false): void {
  const { user, loading: authLoading, isPremium, subscriptionLoading, subscriptionError } = useAuth()

  useEffect(() => {
    const identityKnown = !authLoading
    // A FAILED lookup is not a negative answer. isPremium is false either way,
    // so without this a subscriber whose /api/user/subscription call 500s is
    // labelled registered_free instead of unknown, quietly corrupting the tier
    // dimension. Signed-out visitors owe no lookup, so they are never blocked.
    const entitlementKnown = !user || (!subscriptionLoading && !subscriptionError)
    setAnalyticsUserTier(
      resolveAnalyticsUserTier({
        isAuthenticated: Boolean(user),
        isPremium,
        emailUnlocked: emailTierObserved || undefined,
        tierKnown: identityKnown && entitlementKnown,
      }),
    )
  }, [user, authLoading, isPremium, subscriptionLoading, subscriptionError, emailTierObserved])
}
